/**
 * Shared Platform Links Component
 *
 * Renders platform links with above-fold priority and lightbox for more.
 * Priority order (above fold): Website, Bandcamp, YouTube, Apple Music, Tidal, Spotify, SoundCloud
 * Everything else goes in the "More links" lightbox.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { MusicBrainzLinks } from '@tastymusic/research-agent';
import './PlatformLinks.css';

// Extended links type that includes all possible platforms
export type AnyLinks = Partial<MusicBrainzLinks> & {
  bandcamp?: string;
  spotify?: string;
  apple?: string;
  youtube?: string;
  soundcloud?: string;
  tidal?: string;
  deezer?: string;
  discogs?: string;
  official?: string;
  instagram?: string;
  twitter?: string;
  facebook?: string;
  amazon?: string;
  wikipedia?: string;
  allmusic?: string;
  genius?: string;
  songkick?: string;
  bandsintown?: string;
  lastfm?: string;
};

// Check if a URL is a direct link (not a search URL)
function isDirectLink(url: string | undefined): boolean {
  if (!url) return false;
  if (url.includes('/search')) return false;
  if (url.includes('search?')) return false;
  if (url.includes('search/')) return false;
  if (url.includes('search_query=')) return false;
  if (url.includes('?q=')) return false;
  if (url.includes('?term=')) return false;
  return true;
}

// Generate search URLs for platforms
export function getSearchLinks(artistName: string) {
  const encoded = encodeURIComponent(artistName);
  return {
    bandcamp: `https://bandcamp.com/search?q=${encoded}`,
    spotify: `https://open.spotify.com/search/${encoded}`,
    apple: `https://music.apple.com/search?term=${encoded}`,
    youtube: `https://www.youtube.com/results?search_query=${encoded}`,
    tidal: `https://listen.tidal.com/search?q=${encoded}`,
    soundcloud: `https://soundcloud.com/search?q=${encoded}`,
    amazon: `https://music.amazon.com/search/${encoded}`,
    deezer: `https://www.deezer.com/search/${encoded}`,
    discogs: `https://www.discogs.com/search/?q=${encoded}&type=artist`,
    wikipedia: `https://en.wikipedia.org/wiki/Special:Search?search=${encoded}`,
    lastfm: `https://www.last.fm/search/artists?q=${encoded}`,
  };
}

// Platform configuration
type PlatformConfig = {
  key: string;
  url: string;
  platformClass: string;
  label: string;
  svg: JSX.Element;
  isSearch: boolean;
};

// Priority platforms (above fold) in order
const PRIORITY_PLATFORMS = ['official', 'bandcamp', 'youtube', 'apple', 'tidal', 'spotify', 'soundcloud'] as const;

// All platforms that can appear in the lightbox
const ALL_PLATFORMS = [
  'official', 'bandcamp', 'youtube', 'apple', 'tidal', 'spotify', 'soundcloud',
  'deezer', 'amazon', 'discogs', 'wikipedia', 'allmusic', 'genius', 'lastfm',
  'songkick', 'bandsintown', 'instagram', 'twitter', 'facebook'
] as const;

interface PlatformLinksProps {
  links: AnyLinks;
  artistName?: string;
  size?: 'small' | 'medium';
  showSearchFallbacks?: boolean;
}

export function PlatformLinks({
  links,
  artistName,
  size = 'medium',
  showSearchFallbacks = true
}: PlatformLinksProps) {
  const [showModal, setShowModal] = useState(false);
  const iconSize = size === 'small' ? 16 : 18;
  const search = artistName ? getSearchLinks(artistName) : null;

  // SVG icons for each platform
  const icons: Record<string, JSX.Element> = {
    official: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
      </svg>
    ),
    bandcamp: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M0 18.75l7.437-13.5H24l-7.438 13.5H0z"/>
      </svg>
    ),
    spotify: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
      </svg>
    ),
    apple: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.785-.282-2.442-.966-.322-.335-.534-.73-.612-1.19a2.574 2.574 0 01.142-1.347c.23-.564.635-.976 1.162-1.274.465-.263.97-.4 1.49-.472.376-.053.754-.083 1.132-.117.19-.018.377-.042.562-.08.18-.035.334-.123.335-.34V8.43c0-.14-.05-.2-.19-.22-.402-.063-.805-.116-1.21-.168-.598-.078-1.197-.153-1.796-.227l-2.918-.36c-.082-.01-.164-.026-.246-.028-.107-.002-.16.05-.17.157-.003.03-.004.06-.004.09v7.725c0 .295-.002.59-.038.882-.095.78-.474 1.387-1.15 1.81-.513.32-1.08.48-1.68.523-.936.066-1.78-.178-2.46-.84-.396-.387-.655-.85-.715-1.404a2.69 2.69 0 01.227-1.418c.263-.564.678-.984 1.222-1.274.478-.254.99-.38 1.517-.437.36-.038.723-.06 1.084-.092.296-.026.59-.06.882-.102.093-.013.18-.058.18-.18V5.22c0-.08.018-.15.1-.19.063-.03.138-.026.207-.017l.91.108c.602.073 1.205.145 1.808.218l2.097.255c.66.08 1.32.162 1.98.24l1.166.143c.083.01.165.026.246.044.09.02.132.073.132.167v4.283z"/>
      </svg>
    ),
    youtube: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    soundcloud: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c0-.057-.045-.1-.09-.1m-.899.828c-.06 0-.091.037-.104.094L0 14.479l.165 1.308c0 .055.045.094.09.094s.089-.045.104-.104l.21-1.319-.21-1.334c0-.061-.044-.09-.09-.09m1.83-1.229c-.061 0-.12.045-.12.104l-.21 2.563.225 2.458c0 .06.045.12.119.12.061 0 .105-.061.121-.12l.254-2.474-.254-2.548c-.016-.06-.061-.12-.121-.12m.945-.089c-.075 0-.135.06-.15.135l-.193 2.64.21 2.544c.016.077.075.138.149.138.075 0 .135-.061.15-.15l.24-2.532-.24-2.623c0-.075-.06-.135-.135-.135m1.155.36c-.005-.09-.075-.149-.159-.149-.09 0-.158.06-.164.149l-.217 2.43.2 2.563c0 .09.075.157.159.157.074 0 .148-.068.148-.158l.227-2.563-.227-2.444m.809-.63c-.09 0-.18.09-.18.18l-.165 2.865.18 2.625c0 .09.09.18.18.18.078 0 .167-.09.167-.18l.209-2.64-.192-2.865c0-.09-.089-.18-.18-.18m.959-.12c0-.104-.09-.194-.194-.194-.104 0-.18.09-.195.18l-.164 2.985.164 2.596c.015.104.09.18.194.18.105 0 .18-.09.195-.195l.18-2.58-.165-2.985m.944.374c-.105 0-.18.09-.195.195l-.15 2.55.135 2.505c.015.09.09.18.21.18.104 0 .195-.09.195-.195l.164-2.505-.164-2.549c-.016-.104-.105-.18-.195-.18m.915-.465c-.12 0-.21.104-.21.21l-.137 2.81.135 2.46c0 .12.09.21.21.21.12 0 .21-.09.21-.21l.15-2.46-.15-2.78c0-.12-.09-.24-.21-.24m1.229.45c-.135 0-.24.105-.24.24l-.12 2.355.12 2.414c0 .137.105.24.24.24.12 0 .24-.105.24-.24l.135-2.414-.135-2.37c0-.121-.12-.226-.24-.226m.839-.044c-.135 0-.24.12-.255.255l-.12 2.152.12 2.4c.016.135.12.24.256.24.135 0 .24-.105.255-.255l.12-2.385-.12-2.175c-.015-.135-.12-.24-.256-.24m1.62.42c0-.15-.135-.285-.285-.285-.15 0-.27.135-.285.285l-.106 1.934.106 2.354c.015.15.12.285.285.285.15 0 .27-.12.285-.285l.12-2.354-.12-1.919m.705 4.17c.165 0 .3-.135.3-.3l.105-2.01-.105-2.115c-.015-.165-.135-.3-.3-.3-.165 0-.285.135-.3.3l-.09 2.085.09 2.025c.015.15.135.315.3.315m.66-4.696c-.015-.18-.15-.315-.33-.315-.165 0-.315.135-.315.315l-.105 2.265.105 2.01c0 .18.15.315.315.315.18 0 .315-.135.33-.315l.09-2.025-.09-2.25m.787 4.455c.21 0 .36-.15.375-.345l.09-2.025-.09-2.25c-.015-.18-.18-.33-.36-.33-.194 0-.345.165-.36.345l-.075 2.22.075 2.025c.015.195.165.36.36.36m1.02-.06c.195 0 .375-.165.375-.375l.075-1.92-.075-2.265c0-.21-.18-.375-.375-.375-.21 0-.375.165-.39.375l-.06 2.205.06 1.98c0 .21.18.375.39.375m.87-4.86c-.21 0-.39.18-.405.39l-.06 2.175.06 1.98c.015.21.195.375.405.375.195 0 .375-.165.39-.375l.075-1.98-.075-2.19c-.015-.21-.195-.375-.39-.375m1.275.015c-.225 0-.405.18-.405.405l-.044 2.19.044 1.95c0 .225.18.39.405.39.21 0 .39-.165.405-.375l.06-1.965-.06-2.19c-.015-.225-.195-.405-.405-.405m1.095 4.335c.24 0 .42-.195.435-.42l.045-1.92-.045-2.205c-.015-.225-.195-.42-.42-.42-.24 0-.42.18-.435.405l-.045 2.22.045 1.92c.015.225.195.42.42.42m1.11-5.01c-.24 0-.435.18-.45.42l-.03 2.385.03 1.875c.015.24.21.42.45.42.224 0 .42-.18.434-.42l.046-1.875-.046-2.37c-.015-.24-.21-.45-.435-.45m1.665 4.8c.255 0 .45-.195.465-.45l.03-1.785-.03-2.385c-.015-.255-.21-.45-.465-.45-.239 0-.435.195-.449.45l-.03 2.385.03 1.785c.014.255.21.45.449.45m1.17-5.13c-.255 0-.45.21-.465.465l-.015 2.295.015 1.77c.015.255.21.465.465.465.255 0 .465-.21.465-.465l.03-1.755-.03-2.31c0-.255-.21-.465-.465-.465m1.635 4.98c.27 0 .48-.21.495-.465l.015-1.755-.015-2.31c-.015-.255-.225-.465-.495-.465-.27 0-.48.21-.495.465l-.015 2.31.015 1.755c.015.255.225.465.495.465m1.245-5.205c-.285 0-.495.225-.51.495l-.015 2.085.015 1.74c.015.27.225.495.51.495.27 0 .495-.225.51-.495l.015-1.74-.015-2.085c-.015-.27-.24-.495-.51-.495m1.575-.045c-.27 0-.495.225-.51.495v4.245c.015.255.24.465.51.465.27 0 .495-.21.51-.465v-4.245c-.015-.27-.24-.495-.51-.495m1.695 4.815c.285 0 .51-.24.51-.51v-3.69c0-.285-.225-.51-.51-.51-.27 0-.51.225-.51.51v3.69c0 .27.24.51.51.51m.705-4.695c-.21 0-.39.18-.39.39v3.915c0 .21.18.39.39.39a4.7 4.7 0 0 0 3.465-1.5 4.7 4.7 0 0 0 1.035-2.805 4.695 4.695 0 0 0-4.5-4.695z"/>
      </svg>
    ),
    tidal: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M12.012 3.992L8.008 7.996 4.004 3.992 0 7.996 4.004 12l4.004-4.004L12.012 12l-4.004 4.004 4.004 4.004 4.004-4.004L12.012 12l4.004-4.004-4.004-4.004zm7.992 0l-4.004 4.004 4.004 4.004 4.004-4.004-4.004-4.004z"/>
      </svg>
    ),
    deezer: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M18.81 4.16v3.03H24V4.16h-5.19zM6.27 8.38v3.027h5.189V8.38h-5.19zm12.54 0v3.027H24V8.38h-5.19zM6.27 12.594v3.027h5.189v-3.027h-5.19zm6.271 0v3.027h5.19v-3.027h-5.19zm6.27 0v3.027H24v-3.027h-5.19zM0 16.81v3.029h5.19v-3.03H0zm6.27 0v3.029h5.189v-3.03h-5.19zm6.271 0v3.029h5.19v-3.03h-5.19zm6.27 0v3.029H24v-3.03h-5.19z"/>
      </svg>
    ),
    amazon: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.44-2.186 1.44-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.7-3.182v.683zm3.186 7.705a.659.659 0 01-.75.075c-1.053-.875-1.242-1.281-1.818-2.113-1.739 1.774-2.972 2.305-5.227 2.305-2.669 0-4.749-1.645-4.749-4.94 0-2.574 1.394-4.326 3.379-5.183 1.72-.756 4.125-.891 5.963-1.099v-.412c0-.756.059-1.649-.385-2.302-.385-.579-1.124-.82-1.775-.82-1.206 0-2.28.62-2.543 1.904-.054.285-.261.566-.549.58l-3.063-.33c-.259-.06-.548-.271-.473-.672C6.009 1.554 8.941.19 11.572.19c1.345 0 3.103.357 4.164 1.378 1.346 1.267 1.218 2.958 1.218 4.798v4.343c0 1.306.541 1.879 1.052 2.584.18.252.22.554-.01.74-.575.48-1.601 1.37-2.165 1.87l-.007-.008zM21.779 21.379c-1.934 1.428-4.74 2.19-7.156 2.19-3.384 0-6.432-1.252-8.735-3.336-.181-.164-.019-.387.198-.26 2.487 1.448 5.564 2.318 8.74 2.318 2.143 0 4.5-.448 6.669-1.37.328-.142.602.215.284.458zm.809-.919c-.246-.318-1.632-.15-2.254-.076-.19.022-.218-.142-.048-.262 1.103-.776 2.912-.551 3.123-.291.212.263-.056 2.078-1.09 2.945-.159.133-.31.062-.239-.114.232-.578.752-1.884.508-2.202z"/>
      </svg>
    ),
    discogs: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M12 0C5.383 0 0 5.383 0 12s5.383 12 12 12 12-5.383 12-12S18.617 0 12 0zm0 21.6c-5.3 0-9.6-4.3-9.6-9.6S6.7 2.4 12 2.4s9.6 4.3 9.6 9.6-4.3 9.6-9.6 9.6zm0-16.8c-3.976 0-7.2 3.224-7.2 7.2s3.224 7.2 7.2 7.2 7.2-3.224 7.2-7.2-3.224-7.2-7.2-7.2zm0 12c-2.649 0-4.8-2.151-4.8-4.8S9.351 7.2 12 7.2s4.8 2.151 4.8 4.8-2.151 4.8-4.8 4.8zm0-7.2c-1.324 0-2.4 1.076-2.4 2.4s1.076 2.4 2.4 2.4 2.4-1.076 2.4-2.4-1.076-2.4-2.4-2.4z"/>
      </svg>
    ),
    instagram: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
    twitter: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    facebook: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    wikipedia: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M12.09 13.119c-.936 1.932-2.217 4.548-2.853 5.728-.616 1.074-1.127.931-1.532.029-1.406-3.321-4.293-9.144-5.651-12.409-.251-.601-.441-.987-.619-1.139-.181-.15-.554-.24-1.122-.271C.103 5.033 0 4.982 0 4.898v-.455l.052-.045c.924-.005 5.401 0 5.401 0l.051.045v.434c0 .119-.075.176-.225.176l-.564.031c-.485.029-.727.164-.727.436 0 .135.053.33.166.601 1.082 2.646 4.818 10.521 4.818 10.521l.136.046 2.411-4.81-.482-1.067-1.658-3.264s-.318-.654-.428-.872c-.728-1.443-.712-1.518-1.447-1.617-.207-.023-.313-.05-.313-.149v-.468l.06-.045h4.292l.113.037v.451c0 .105-.076.15-.227.15l-.308.047c-.792.061-.661.381-.136 1.422l1.582 3.252 1.758-3.504c.293-.64.233-.801.111-.947-.07-.084-.305-.22-.812-.24l-.201-.021c-.052 0-.098-.015-.145-.051-.045-.031-.067-.076-.067-.129v-.427l.061-.045c1.247-.008 4.043 0 4.043 0l.059.045v.436c0 .121-.059.178-.193.178-.646.03-.782.095-1.023.439-.12.186-.375.589-.646 1.039l-2.301 4.273-.065.135 2.792 5.712.17.048 4.396-10.438c.154-.422.129-.722-.064-.895-.197-.172-.346-.273-.857-.295l-.42-.016c-.061 0-.105-.014-.152-.045-.043-.029-.072-.075-.072-.119v-.436l.059-.045h4.961l.041.045v.437c0 .119-.074.18-.209.18-.648.03-1.127.18-1.443.421-.314.255-.557.616-.736 1.067 0 0-4.043 9.258-5.426 12.339-.525 1.007-1.053.917-1.503-.031-.571-1.171-1.773-3.786-2.646-5.71l.053-.036z"/>
      </svg>
    ),
    allmusic: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22c-5.514 0-10-4.486-10-10S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10zm-2-15l7 5-7 5V7z"/>
      </svg>
    ),
    genius: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M0 7.845v1.722h14.018v-.122h-.122c-.488-.061-.731-.305-.731-.793v-5.86c0-.488.243-.732.731-.793h.122V.277H8.35v1.722h.122c.488.061.731.305.731.793v3.651H3.574V2.792c0-.488.244-.732.732-.793h.122V.277H0v1.722h.122c.488.061.731.305.731.793v12.416c0 .488-.243.732-.731.793H0v1.722h4.428v-1.722h-.122c-.488-.061-.732-.305-.732-.793V9.568h5.63v5.64c0 .488-.244.732-.732.793h-.122v1.722h5.668v-1.722h-.122c-.488-.061-.731-.305-.731-.793v-5.64H24V7.845H0z"/>
      </svg>
    ),
    lastfm: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M10.584 17.21l-.88-2.392s-1.43 1.594-3.573 1.594c-1.897 0-3.244-1.649-3.244-4.288 0-3.382 1.704-4.591 3.381-4.591 2.42 0 3.189 1.567 3.849 3.574l.88 2.749c.88 2.666 2.529 4.81 7.285 4.81 3.409 0 5.718-1.044 5.718-3.793 0-2.227-1.265-3.381-3.63-3.932l-1.758-.385c-1.21-.275-1.567-.77-1.567-1.595 0-.934.742-1.484 1.952-1.484 1.32 0 2.034.495 2.144 1.677l2.749-.33c-.22-2.474-1.924-3.492-4.729-3.492-2.474 0-4.893.935-4.893 3.932 0 1.87.907 3.051 3.189 3.601l1.87.44c1.402.33 1.869.907 1.869 1.704 0 1.017-.99 1.43-2.86 1.43-2.776 0-3.932-1.457-4.59-3.464l-.907-2.75c-1.155-3.573-2.997-4.893-6.653-4.893C2.144 5.333 0 7.89 0 12.233c0 4.18 2.144 6.434 5.993 6.434 3.106 0 4.591-1.457 4.591-1.457z"/>
      </svg>
    ),
    songkick: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M6.55 21.556H3.095L.025 12.29h3.186l1.84 5.635 1.839-5.635h3.186L6.55 21.556zM24 2.444v2.611h-4.018v2.533H24v2.611h-4.018v2.534H24v2.611h-4.018v5.212h-2.789V2.444H24zm-10.564 0v5.144h-.078l-2.867-5.144H7.41v16.389h2.789V13.69h.078l2.867 5.143h3.082V2.444h-2.79z"/>
      </svg>
    ),
    bandsintown: (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="currentColor">
        <path d="M0 24V0h6v24H0zm9 0V6h6v18H9zm9 0V0h6v24h-6z"/>
      </svg>
    ),
  };

  // Platform display names
  const platformLabels: Record<string, string> = {
    official: 'Website',
    bandcamp: 'Bandcamp',
    spotify: 'Spotify',
    apple: 'Apple Music',
    youtube: 'YouTube',
    soundcloud: 'SoundCloud',
    tidal: 'Tidal',
    deezer: 'Deezer',
    amazon: 'Amazon Music',
    discogs: 'Discogs',
    instagram: 'Instagram',
    twitter: 'X',
    facebook: 'Facebook',
    wikipedia: 'Wikipedia',
    allmusic: 'AllMusic',
    genius: 'Genius',
    lastfm: 'Last.fm',
    songkick: 'Songkick',
    bandsintown: 'Bandsintown',
  };

  // Build link configs
  const getUrl = (platform: string): string | null => {
    const directUrl = links[platform as keyof AnyLinks];
    if (directUrl && typeof directUrl === 'string' && isDirectLink(directUrl)) {
      return directUrl;
    }
    return null;
  };

  const getSearchUrl = (platform: string): string | null => {
    if (!showSearchFallbacks || !search) return null;
    const searchUrl = search[platform as keyof ReturnType<typeof getSearchLinks>];
    return searchUrl || null;
  };

  // Build above-fold links (priority platforms, direct links only)
  const aboveFoldLinks: PlatformConfig[] = [];
  for (const platform of PRIORITY_PLATFORMS) {
    const url = getUrl(platform);
    if (url && icons[platform]) {
      aboveFoldLinks.push({
        key: platform,
        url,
        platformClass: platform,
        label: platformLabels[platform] || platform,
        svg: icons[platform],
        isSearch: false,
      });
    }
  }

  // Build modal links - ALL direct links first, then search links
  const directLinks: PlatformConfig[] = [];
  const searchLinks: PlatformConfig[] = [];

  for (const platform of ALL_PLATFORMS) {
    const directUrl = getUrl(platform);
    if (directUrl && icons[platform]) {
      directLinks.push({
        key: platform,
        url: directUrl,
        platformClass: platform,
        label: platformLabels[platform] || platform,
        svg: icons[platform],
        isSearch: false,
      });
    } else {
      const searchUrl = getSearchUrl(platform);
      if (searchUrl && icons[platform]) {
        searchLinks.push({
          key: `${platform}-search`,
          url: searchUrl,
          platformClass: platform,
          label: platformLabels[platform] || platform,
          svg: icons[platform],
          isSearch: true,
        });
      }
    }
  }

  const hasAboveFold = aboveFoldLinks.length > 0;
  const hasModalLinks = directLinks.length > 0 || searchLinks.length > 0;

  return (
    <div className={`platform-links-container ${size === 'small' ? 'small' : ''}`}>
      {/* Above fold links - icons only, no labels */}
      {hasAboveFold && (
        <div className="platform-links above-fold">
          {aboveFoldLinks.map(config => (
            <a
              key={config.key}
              href={config.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`platform-link ${config.platformClass} direct`}
              title={config.label}
            >
              {config.svg}
            </a>
          ))}
        </div>
      )}

      {/* More links button */}
      {hasModalLinks && (
        <button
          className="more-links-button"
          onClick={() => setShowModal(true)}
        >
          <svg viewBox="0 0 24 24" width={12} height={12} fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          {hasAboveFold ? 'More Links' : 'Links'}
        </button>
      )}

      {/* Links modal - rendered via portal to escape stacking context issues */}
      {showModal && createPortal(
        <div className="links-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="links-modal" onClick={e => e.stopPropagation()}>
            <div className="links-modal-header">
              <h3>Links</h3>
              <button className="links-modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="links-modal-content">
              {/* Direct links - two columns */}
              {directLinks.length > 0 && (
                <div className="modal-links-grid">
                  {directLinks.map(config => (
                    <a
                      key={config.key}
                      href={config.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`modal-link ${config.platformClass} direct`}
                    >
                      <span className="modal-link-icon">{config.svg}</span>
                      <span className="modal-link-label">{config.label}</span>
                    </a>
                  ))}
                </div>
              )}

              {/* Search section */}
              {searchLinks.length > 0 && (
                <>
                  <div className="modal-section-header">Search</div>
                  <div className="modal-links-grid">
                    {searchLinks.map(config => (
                      <a
                        key={config.key}
                        href={config.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`modal-link ${config.platformClass} search`}
                      >
                        <span className="modal-link-icon">{config.svg}</span>
                        <span className="modal-link-label">{config.label}</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
